import React, { useState, useEffect } from 'react';

function ConsultationModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    privacy: false,
    page: '',
    source: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Function to handle consultation button clicks
    const handleConsultationClick = (e) => {
      const target = e.target.closest('[data-open-consultation]');
      if (target) {
        e.preventDefault();
        const sourceLabel = target.dataset.sourceLabel || target.dataset.source || target.textContent.trim();
        setFormData(prev => ({
          ...prev,
          source: sourceLabel,
          page: window.location.href
        }));
        setIsOpen(true);
        document.body.classList.add('modal-open');
      }
    };

    // Add event listener to document
    document.addEventListener('click', handleConsultationClick);

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleConsultationClick);
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const closeModal = () => {
    setIsOpen(false);
    document.body.classList.remove('modal-open');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const showNotification = (message, type = 'info') => {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll('.notification');
    existingNotifications.forEach(notification => notification.remove());

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;

    // Style the notification
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '1rem 1.5rem',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '500',
      zIndex: '10000',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease',
      maxWidth: '300px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    });

    // Set background color based on type
    switch (type) {
      case 'success':
        notification.style.background = 'linear-gradient(135deg, #10b981, #059669)';
        break;
      case 'error':
        notification.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)';
        break;
      default:
        notification.style.background = 'linear-gradient(135deg, #3b82f6, #2563eb)';
    }

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    // Auto remove after 5 seconds
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 5000);
  };

  const submitToSlack = async (formData) => {
    try {
      // Use the serverless API endpoint to avoid CORS issues
      const apiUrl = '/api/send-to-slack';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          source: formData.source,
          page: formData.page
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      return result.success;
    } catch (error) {
      console.error('Error submitting to Slack:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      showNotification('Bitte geben Sie Ihren Namen ein.', 'error');
      return;
    }

    if (!formData.email.trim() && !formData.phone.trim()) {
      showNotification('Bitte geben Sie eine E-Mail-Adresse oder Telefonnummer an.', 'error');
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      showNotification('Bitte geben Sie eine gültige E-Mail-Adresse ein.', 'error');
      return;
    }

    if (!formData.message.trim()) {
      showNotification('Bitte teilen Sie uns Ihr Anliegen mit.', 'error');
      return;
    }

    if (!formData.privacy) {
      showNotification('Bitte akzeptieren Sie die Datenschutzbestimmungen.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await submitToSlack(formData);
      if (success) {
        showNotification('Vielen Dank! Wir melden uns zeitnah bei Ihnen.', 'success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          privacy: false,
          page: '',
          source: ''
        });
        closeModal();
      } else {
        showNotification('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.', 'error');
      }
    } catch (error) {
      console.error('Error submitting consultation form:', error);
      showNotification('Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`consultation-modal ${isOpen ? 'is-open' : ''}`} aria-hidden={!isOpen}>
      <div className="consultation-modal__backdrop" onClick={closeModal}></div>
      <div className="consultation-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="consultation-modal-title" tabIndex="-1">
        <button className="consultation-modal__close" type="button" aria-label="Modal schließen" onClick={closeModal}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>

        <div className="consultation-modal__layout">
          {/* Left Side - Benefits & Trust */}
          <div className="consultation-modal__benefits">
            <div className="consultation-modal__header">
              <h2 id="consultation-modal-title" className="consultation-modal__title">
                Kostenloses Erstgespräch
              </h2>
              <p className="consultation-modal__subtitle">
                Ihre Social-Recruiting-Strategie in 15 Minuten
              </p>
            </div>

            <div className="consultation-modal__trust">
              <p className="trust-text-compact">Kanal-Empfehlung • Kosten-Kalkulation • 48h-Antwort • 500+ Kampagnen</p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="consultation-modal__form-wrapper">
            <div className="form-wrapper__header">
              <h3>Jetzt unverbindlich anfragen</h3>
            </div>
            <form className="consultation-form" noValidate onSubmit={handleSubmit}>
            <div className="consultation-form__field">
              <label htmlFor="consultation-name">
                Ihr Name <span className="required">*</span>
              </label>
              <input 
                type="text" 
                id="consultation-name" 
                name="name" 
                autoComplete="name" 
                required 
                placeholder="Max Mustermann"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            <div className="consultation-form__row">
              <div className="consultation-form__field">
                <label htmlFor="consultation-email">E-Mail-Adresse</label>
                <input 
                  type="email" 
                  id="consultation-email" 
                  name="email" 
                  autoComplete="email" 
                  placeholder="max@unternehmen.de"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="consultation-form__field">
                <label htmlFor="consultation-phone">Telefon</label>
                <input 
                  type="tel" 
                  id="consultation-phone" 
                  name="phone" 
                  autoComplete="tel" 
                  placeholder="+49 30 ..."
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <p className="consultation-form__note">Mindestens E-Mail oder Telefon erforderlich</p>
            <div className="consultation-form__field">
              <label htmlFor="consultation-message">
                Ihr Anliegen <span className="required">*</span>
              </label>
              <textarea 
                id="consultation-message" 
                name="message" 
                rows="4" 
                required 
                placeholder="Beschreiben Sie kurz Ihre Recruiting-Herausforderung..."
                value={formData.message}
                onChange={handleInputChange}
              ></textarea>
            </div>
            <div className="consultation-form__privacy-wrapper">
              <div className="consultation-form__privacy-check">
                <input 
                  type="checkbox" 
                  id="consultation-privacy" 
                  name="privacy" 
                  required
                  checked={formData.privacy}
                  onChange={handleInputChange}
                />
                <label htmlFor="consultation-privacy">
                  Ich stimme zu, dass meine Daten zur Bearbeitung meiner Anfrage verwendet werden. Die Datenverarbeitung erfolgt gemäß unserer <a href="/datenschutz/" target="_blank">Datenschutzerklärung</a>. Eine Weitergabe an Dritte erfolgt nicht. <span className="required">*</span>
                </label>
              </div>
            </div>
            <button 
              type="submit" 
              className="btn btn--primary btn--full consultation-form__submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Wird gesendet...' : 'Jetzt kostenloses Erstgespräch anfragen'}
            </button>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsultationModal;
